import React, { useMemo } from 'react';
import styled from '@cthings.co/styled-components';
import { Modal } from '@bit/first-scope.modal';
import { ModalContainer } from '@bit/first-scope.modal-container';
import { Text, TextType } from '@bit/first-scope.text';
import { CommonButton } from '@cthings.co/buttons';
import { media } from '@cthings.co/styles-utils';
import { CloseButtonRound } from '../../../../containers/common/reports/components/closeButtonRound/CloseButtonRound';
import { Dropdown } from '../../../dropdown/Dropdown';
import { TableInput, TableInputType } from '../../../tableInput/TableInput';
import { useBlockBodyScroll } from '../../../../utils/useBlockBodyScroll';
import { formatStringToDate, getEndDate, getStartDate } from '../header/Header';
import { useTheme } from '@cthings.co/styled-components';

import { colorFetchFDS as colorFetch } from '@cthings.co/styles-utils';
import { GraphType } from '../../types';

// @ts-ignore
const StyledModal = styled(Modal)`
  ${media.semiTablet} {
    align-items: flex-end;
    & > div {
      width: 100%;
      align-items: flex-end;
    }
  }
` as any; // @TODO fix commponent type

// @ts-ignore
const StyledContainer = styled(ModalContainer)`
  width: 528px;
  align-items: flex-start;
  padding: 24px;

  ${media.semiTablet} {
    width: 100%;
    border-radius: 24px 24px 0 0;
    padding: 16px;
  }
`;

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// @ts-ignore
const Title = styled(Text)`
  font-size: 18px;
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 22px;
`;

const StyledDropdown = styled(Dropdown)`
  margin-bottom: 10px;
  & > span {
    visibility: visible !important;
  }
  & > div {
    width: 100%;
    height: 40px;
  }
`;

type DateRangeProps = {
  isDateAvailable: boolean;
};

const DateRange = styled(TableInput)<DateRangeProps>`
  pointer-events: ${({ isDateAvailable }) => (isDateAvailable ? 'auto' : 'none')};
  & > div > div {
    width: calc(50% - 8px);
    & > div {
      width: 100%;
      & > div > div > div {
        height: 40px;
        & > div > svg > path {
          stroke: ${({ isDateAvailable, theme }) =>
            isDateAvailable ? colorFetch('gray1')({ theme }) : colorFetch('gray3')({ theme })};
        }
        & > span {
          color: ${({ isDateAvailable, theme }) =>
            isDateAvailable ? colorFetch('gray1')({ theme }) : colorFetch('gray3')({ theme })};
          font-weight: 400 !important;
        }
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 14px;
`;

type ButtonProps = {
  isApplyAvailable: boolean;
};

// @ts-ignore
const Button = styled(CommonButton)<ButtonProps>`
  width: 100%;
  height: 40px;
  &.first {
    margin: 0 0 10px 0;
    pointer-events: ${({ isApplyAvailable }) => (isApplyAvailable ? 'auto' : 'none')};
    user-select: ${({ isApplyAvailable }) => (isApplyAvailable ? 'auto' : 'none')};
    cursor: ${({ isApplyAvailable }) => (isApplyAvailable ? 'pointer' : 'auto')};
  }
`;

export const FiltersModal = ({
  isOpenModal,
  closeModal,
  handleApply,
  filters,
  setCurrentFilters,
  setFilters,
  graphType,
  applyingStatus,
  isApplyButtonActive,
  languageStrings,
}: {
  isOpenModal?: boolean;
  closeModal: () => void;
  handleApply: () => void;
  filters: any;
  setCurrentFilters: any;
  setFilters: (filterName: string, value: { tooltip: string; id: string; key: string }) => void;
  graphType: GraphType;
  applyingStatus: number;
  isApplyButtonActive: boolean;
  languageStrings?: any;
}) => {
  const theme = useTheme();
  const { primary, secondary1, pureWhite, black1, gray3 } = theme.colors;
  const filterErrorMessage = languageStrings.filtersSelectError;

  const period = filters.periods?.default_value;
  const interval = filters.intervals?.default_value;
  const method = filters.averaging_methods?.default_value;
  const periodListRedux = filters.periods?.items_list;
  const intervalList = filters.intervals?.items_list;
  const methodList = filters.averaging_methods?.items_list;
  const isApplyButtonAvailable = isApplyButtonActive && !!(period && interval && method);

  const prepareArr = (arr: any[]) =>
    (arr || []).map((item: any) => ({ name: item.tooltip, id: item.id, key: item.key }));
  const findKey = (arr: any[], id: string) => (arr || []).find((item: any) => item.id === id).key;

  const applyButtonText =
    applyingStatus === 0
      ? languageStrings.applyButton
      : applyingStatus === 1
      ? languageStrings.applyButton1
      : languageStrings.applyButton2;

  const periodOnChangeArr = useMemo(
    () => [
      () => {
        setCurrentFilters((filters: any) => ({
          ...filters,
          ts_from: getStartDate(new Date(new Date().setDate(new Date().getDate() - 1))),
          ts_to: getEndDate(new Date()),
        }));
      },
      () => {
        setCurrentFilters((filters: any) => ({
          ...filters,
          ts_from: getStartDate(new Date(new Date().setDate(new Date().getDate() - 7))),
          ts_to: getEndDate(new Date()),
        }));
      },
      () => {
        setCurrentFilters((filters: any) => ({
          ...filters,
          ts_from: getStartDate(new Date(new Date().setMonth(new Date().getMonth() - 1))),
          ts_to: getEndDate(new Date()),
        }));
      },
      () => {
        setCurrentFilters((filters: any) => ({
          ...filters,
          ts_from: getStartDate(new Date(new Date().setMonth(new Date().getMonth() - 6))),
          ts_to: getEndDate(new Date()),
        }));
      },
    ],
    [setCurrentFilters],
  );

  const periodList = (periodListRedux || []).map((item: any, index: number, arr: any[]) =>
    index < arr.length - 1
      ? { name: item.tooltip, id: item.id, key: item.key, onClick: periodOnChangeArr[index] }
      : { name: item.tooltip, id: item.id, key: item.key },
  );

  useBlockBodyScroll(!!isOpenModal);

  const dropdowns = [
    {
      value: interval,
      valueList: prepareArr(intervalList),
      fieldName: languageStrings.pointIntervalLabel,
      onChange: (value: { tooltip: string; id: string; key: string }) => {
        setFilters('intervals', value);
      },
    },
    {
      value: method,
      valueList: prepareArr(methodList),
      fieldName: languageStrings.averagingMethodLabel,
      onChange: (value: { tooltip: string; id: string; key: string }) => {
        setFilters('averaging_methods', value);
      },
    },
    {
      value: period,
      valueList: periodList,
      fieldName: languageStrings.dataPeriodLabel,
      onChange: (value: { tooltip: string; id: string; key: string }) => {
        setFilters('periods', value);
      },
    },
  ];

  const buttons = [
    {
      value: applyButtonText,
      backgroundColor: isApplyButtonAvailable ? primary : gray3,
      backgroundColorHover: isApplyButtonAvailable ? secondary1 : gray3,
      borderColor: isApplyButtonAvailable ? primary : gray3,
      borderColorHover: isApplyButtonAvailable ? secondary1 : gray3,
      color: pureWhite,
      onClick: handleApply,
    },
    {
      value: languageStrings.insightDetailsDeviceConfigurationEditInputsButtonsCancel,
      backgroundColor: pureWhite,
      borderColor: primary,
      color: black1,
      onClick: () => {
        closeModal();
      },
    },
  ];

  return (
    <StyledModal isOpenModal={isOpenModal}>
      <StyledContainer>
        <TitleWrapper>
          <Title type={TextType.TITLE_PRIMARY}>{languageStrings.filtersButton}</Title>
          <CloseButtonRound closeOnClick={closeModal} />
        </TitleWrapper>
        <Wrapper>
          {dropdowns.map((item: any) => {
            const { value, valueList, fieldName, onChange } = item || {};
            const { id, tooltip /*, key*/ } = value || {};

            return (
              <StyledDropdown
                key={id}
                // @TODO check value arguments
                value={{ name: tooltip ? tooltip : filterErrorMessage, id /*, key*/ }}
                valuesList={valueList}
                fieldName={fieldName}
                onChange={(value: { name: string; id: string }) => {
                  const key: string = findKey(valueList, value.id);
                  onChange({ tooltip: value.name, id: value.id, key });
                }}
                placeholder={fieldName}
                languageStrings={languageStrings}
              />
            );
          })}
          <DateRange
            type={TableInputType.DATE_RANGE_WITH_ICON}
            value={{ startDate: new Date(filters?.ts_from), endDate: new Date(filters?.ts_to) }}
            handleChangeValue={{
              setStartDate: (value: any) => {
                setFilters('periods', { tooltip: periodList[4].name, id: periodList[4].id, key: periodList[4].key });
                setCurrentFilters((filters: any) => ({ ...filters, ts_from: getStartDate(value) }));
              },
              setEndDate: (value: any) => {
                setFilters('periods', { tooltip: periodList[4].name, id: periodList[4].id, key: periodList[4].key });
                setCurrentFilters((filters: any) => ({ ...filters, ts_to: getEndDate(value) }));
              },
            }}
            options={{
              title: languageStrings.selectedDatesLabel,
              startOptions: {
                maxValue: new Date(
                  new Date(formatStringToDate(filters?.ts_to)).setDate(
                    formatStringToDate(filters?.ts_to).getDate() - 1,
                  ),
                ),
                isDisabled: applyingStatus === 1,
              },
              endOptions: {
                maxValue: new Date(new Date().setDate(new Date().getDate() + 1)),
                minValue: new Date(
                  new Date(formatStringToDate(filters?.ts_from)).setDate(
                    formatStringToDate(filters?.ts_from).getDate() + 1,
                  ),
                ),
                isDisabled: applyingStatus === 1,
              },
              bottom: '434px',
            }}
            isDateAvailable={!!period}
            isEditInProcess
          />
        </Wrapper>
        <ButtonWrapper>
          {buttons.map((item: any, index: number) => {
            const {
              value,
              onClick,
              backgroundColor,
              backgroundColorHover,
              borderColor,
              borderColorHover,
              color,
            } = item;
            return (
              <Button
                className={index === 0 ? 'first' : ''}
                onClick={onClick}
                colors={{
                  main: color,
                  background: backgroundColor,
                  backgroundHover: backgroundColorHover,
                  border: borderColor,
                  borderHover: borderColorHover,
                }}
                isApplyAvailable={isApplyButtonAvailable}
              >
                {value}
              </Button>
            );
          })}
        </ButtonWrapper>
      </StyledContainer>
    </StyledModal>
  );
};
